'use client'

import * as React from 'react'

import { cn } from '@/lib/utils/index'

// The one segmented toggle (STYLE_GUIDE.md → Controls): a square hairline box whose active
// segment is a solid foreground chip that slides between options. OptionSwitch and
// ToggleSwitcher are thin wrappers over this, so the shape is defined exactly once.
export interface SegmentedOption<T extends string> {
  value: T
  label: string
  icon?: React.ReactNode
  // Trailing chip after the label (e.g. a "-17%" savings tag). Drawn in currentColor so it
  // inverts with the segment when active.
  badge?: React.ReactNode
  // Greys out just this segment; the control-level `disabled` still wins.
  disabled?: boolean
  // Bare trailing node after the label (e.g. a plan star).
  suffix?: React.ReactNode
  // Wraps the rendered segment, e.g. in a tooltip that must outlive `disabled`.
  wrap?: (segment: React.ReactElement) => React.ReactNode
  // Trading-side semantics for a Buy/Sell switch: the active chip takes chart-mint (buy) or
  // chart-red (sell) instead of the neutral emphasis fill. Nothing else may recolor a segment.
  tone?: 'buy' | 'sell'
}

const TONE_CHIP = {
  buy: 'bg-chart-mint',
  sell: 'bg-chart-red',
} as const

const TONE_TEXT = {
  buy: 'text-ox-ink',
  sell: 'text-ox-paper',
} as const

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  // No longer used: the chip is a persistent in-group indicator, not a shared-layout element.
  // Kept so existing callers still type-check.
  layoutId?: string
  size?: 'xs' | 'sm' | 'md'
  // Render only the icon; the label becomes the tooltip / accessible name.
  iconOnly?: boolean
  // Stretch segments to fill the container (form rows) instead of hugging content.
  fill?: boolean
  disabled?: boolean
  className?: string
}

// Segment heights leave 2px for the group's hairline, so the outer box matches the
// same-named field and Button sizes: xs 24px (dense toolbars), sm 32px, md 40px.
const SIZE = {
  xs: 'h-5.5 px-2 text-xs',
  sm: 'h-7.5 px-2.5 text-xs',
  md: 'h-9.5 px-3.5 text-sm',
} as const

// The active chip is ONE indicator that stays mounted inside the group and slides with a CSS
// transition on translateX/width only, measured relative to the group itself. It used to be a
// framer-motion `layoutId` element remounted into each segment: that projects between
// viewport-measured boxes, so any layout shift during the click (a field appearing below, the
// widget's scroll area reflowing) leaked into the animation as a vertical dip. Measuring
// offsetLeft/offsetWidth inside the group has no vertical component to get wrong, and a CSS
// transition retargets smoothly when clicks land mid-slide.
const CHIP_TRANSITION =
  'transform 200ms cubic-bezier(0.23, 1, 0.32, 1), width 200ms cubic-bezier(0.23, 1, 0.32, 1)'

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
  iconOnly = false,
  fill = false,
  disabled = false,
  className,
}: SegmentedControlProps<T>) {
  const groupRef = React.useRef<HTMLDivElement>(null)
  const [chip, setChip] = React.useState<{ left: number; width: number } | null>(null)
  // The first placement snaps; only later moves slide (no slide-in from the left on mount).
  const [animate, setAnimate] = React.useState(false)
  const activeOption = options.find((option) => option.value === value)

  React.useLayoutEffect(() => {
    const group = groupRef.current
    if (!group) return
    const measure = () => {
      const segment = group.querySelector<HTMLElement>(
        `[data-segment-value="${CSS.escape(String(value))}"]`
      )
      setChip(segment ? { left: segment.offsetLeft, width: segment.offsetWidth } : null)
    }
    measure()
    // Segment widths change with the container (fill) or with label/font loading.
    const observer = new ResizeObserver(measure)
    observer.observe(group)
    group.querySelectorAll('[data-segment-value]').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [value, options])

  React.useEffect(() => {
    if (chip && !animate) {
      const frame = requestAnimationFrame(() => setAnimate(true))
      return () => cancelAnimationFrame(frame)
    }
  }, [chip, animate])

  return (
    <div
      ref={groupRef}
      role="group"
      className={cn(
        'border-foreground/15 relative flex items-stretch overflow-hidden rounded-md border',
        className
      )}
    >
      {chip && activeOption && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 motion-reduce:transition-none!',
            activeOption.tone ? TONE_CHIP[activeOption.tone] : 'bg-emphasis',
            (disabled || activeOption.disabled) && 'opacity-40'
          )}
          style={{
            width: chip.width,
            transform: `translateX(${chip.left}px)`,
            transition: animate ? CHIP_TRANSITION : 'none',
          }}
        />
      )}
      {options.map((option) => {
        const active = option.value === value
        const segment = (
          <button
            key={option.value}
            type="button"
            data-segment-value={String(option.value)}
            disabled={disabled || option.disabled}
            title={iconOnly ? option.label : undefined}
            aria-label={iconOnly ? option.label : undefined}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'relative flex items-center justify-center gap-1.5 font-medium whitespace-nowrap transition-colors not-disabled:cursor-pointer disabled:opacity-40',
              SIZE[size],
              fill && 'flex-1',
              active
                ? option.tone
                  ? TONE_TEXT[option.tone]
                  : 'text-emphasis-foreground'
                : 'text-foreground/55 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-foreground'
            )}
          >
            {option.icon && <span className="relative z-10 flex">{option.icon}</span>}
            {!iconOnly && <span className="relative z-10">{option.label}</span>}
            {!iconOnly && option.badge != null && (
              <span className="relative z-10 border border-current px-1 py-0.5 text-[11px] leading-none">
                {option.badge}
              </span>
            )}
            {option.suffix != null && <span className="relative z-10 flex">{option.suffix}</span>}
          </button>
        )
        return option.wrap ? (
          <React.Fragment key={option.value}>{option.wrap(segment)}</React.Fragment>
        ) : (
          segment
        )
      })}
    </div>
  )
}
